// @ts-expect-error Missing types
import jsreport from '@jsreport/browser-client';
import { type SILogReport } from '@/reports/models/report';
import { type ReportServerConfig } from '@/reports/models/report';
import NoFilter from '../../shared/NoFilter.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { ReportType } from 'shared/reports/domain/reportType';
import { useHttpClient } from '@/common/api/httpClient';
import { useDarkMode } from 'shared/themes/composables/darkMode';
import { useThemeStore } from 'shared/themes/stores/themeStore';

interface LogYardStatusData {
  name: string;
  product: string;
  numberOfLogs: number;
  volume: number;
  minAge: number;
  maxAge: number;
  averageAge: number;
}

interface ReportData {
  darkMode: boolean;
  tiYardTheme: boolean;
  sumNumberOfLogs: number;
  sumVolume: string;
  storageAreas: ReportDataStorageArea[];
}

interface ReportDataStorageArea {
  name: string;
  product: string;
  numberOfLogs: number;
  volume: string;
  minAge: number;
  maxAge: number;
  averageAge: number;
}

const REPORT_TYPE = ReportType.LogYardStatus;
const TEMPLATE_NAME = 'log_yard_status';

export function useStatusReport(): SILogReport {
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  async function fetchDataFromServer(config: ReportServerConfig): Promise<ReportData | null> {
    const httpClient = useHttpClient();
    const headers = httpClient.defaultHeaders();

    const request = new Request(httpClient.siteUrl(`report/${REPORT_TYPE}`), {
      method: 'POST',
      headers,
    });

    const response = await httpClient.execute(request);

    if (response.ok) {
      const reportDataFromServer = (await response.json()) as LogYardStatusData[];

      jsreport.serverUrl = config.reportUrl;
      if (config.authorization) {
        jsreport.headers['Authorization'] = config.authorization;
      }

      const darkMode = useDarkMode();
      const { enabled } = storeToRefs(darkMode);
      const themeStore = useThemeStore();
      const theme = themeStore.currentTheme;


      const reportData: ReportData = {
        darkMode: enabled.value,
        tiYardTheme: theme === 'TiYard',
        sumNumberOfLogs: 0,
        sumVolume: '0.0',
        storageAreas: [],
      };

      let sumLogs = 0;
      let sumVolume = 0.0;
      for (const area of reportDataFromServer) {
        sumLogs += area.numberOfLogs;
        sumVolume += area.volume;
        reportData.storageAreas.push({
          name: area.name,
          product: area.product,
          numberOfLogs: area.numberOfLogs,
          volume: area.volume.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
          minAge: area.minAge,
          maxAge: area.maxAge,
          averageAge: area.averageAge,
        });
      }

      reportData.sumNumberOfLogs = sumLogs;
      reportData.sumVolume = sumVolume.toLocaleString(languageStore.language.code, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

      reportData.storageAreas.sort((a, b) =>
        a.name.localeCompare(b.name, languageStore.language.code, {
          numeric: true,
          sensitivity: 'base',
        }),
      );

      return reportData;
    }

    return null;
  }

  return {
    name: translations.value.reports.reportName(REPORT_TYPE),
    type: REPORT_TYPE,

    exportFormats: [
      {
        label: translations.value.reports.exportFormat.pdf,
        icon: 'pi pi-file-pdf',
        format: 'pdf',
      },
      {
        label: translations.value.reports.exportFormat.excel,
        icon: 'pi pi-file-excel',
        format: 'excel',
      },
    ],
    filterComponent: NoFilter,

    async showReport(config: ReportServerConfig | null): Promise<string> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: 'html',
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          let url = await report.toDataURI();
          url = url.replace(';base64,', ';charset=utf-8;base64,');
          return url;
        } catch (error) {
          console.error('Error generating report:', error);
          return '';
        }
      }

      return '';
    },

    async exportReport(config: ReportServerConfig | null, format: string): Promise<boolean> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      let recipe = 'html';
      let extension = '.txt';
      if (format === 'pdf') {
        recipe = 'chrome-pdf';
        extension = 'pdf';
      } else if (format === 'excel') {
        recipe = 'html-to-xlsx';
        extension = 'xlsx';
      }

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: recipe,
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          report.download(`${TEMPLATE_NAME}.${extension}`);
          return true;
        } catch (error) {
          console.error('Error exporting report:', error);
        }
      }

      return false;
    },
  };
}
